import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Vidal DrugReference model, as recieved via search endpoint.
export type VidalDrugReference = {
  vidal_id: string;
  title: string;
  per_volume: string;
  type: 'product' | 'vmp';
};
type QuerySearchResponse = {
  entries: VidalDrugReference[];
}

export type AdministrationRoute = string

type ATCClassification = {
  id: string;
  code: string;
  title: string;
  level: number;
};

// DrugReference model in PC, after we've added it to a tenant.
export type DrugReference = {
  id: string;
  tenant_id: string;
  commercial_name: string;
  quantity_per_volume: string;
  administration_routes: AdministrationRoute[];
  preferred_administration_route: AdministrationRoute; // Controls which route is chosen.
  atc_classification: ATCClassification[];
  level2?: ATCClassification; // We use this level for "Classification" column. Extracted from atc_classification post format.
  level5?: ATCClassification; // We use this level for "Active Principle" column. Extracted from atc_classification post format.
};

const formatDrugReference = (drugReference: DrugReference): DrugReference => {
  const level2 = drugReference.atc_classification.find((classification) => classification.level === 2);
  const level5 = drugReference.atc_classification.find((classification) => classification.level === 5);
  return {
    ...drugReference,
    level2,
    level5,
  };
};

const ENDPOINTS = {
  search: '/drug_reference/search/', // GET, query sent as "search" query param.
  add: '/drug_reference/', // POST, tenantid and vidal_id sent via body.
  list: '/drug_reference/', // GET, tenantid sent as "tenant_id" query param.
  fromIds: '/drug_reference/from_ids/', // GET, repeated "ids" query params; returns display names only.
  edit: (drugReferenceId: string) => `/drug_reference/${drugReferenceId}/`, // PUT, body for edit payload (currently only "preferred_administration_route_id")
  delete: (drugReferenceId: string) => `/drug_reference/${drugReferenceId}/`, // DELETE, no additional params or body needed.
};

// Placeholder for now.
export const drugReferencesApi = createApi({
  reducerPath: 'drugReferencesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api',
    prepareHeaders: (headers) => {
      headers.set('Accept', 'application/json');
      headers.set('X-CSRF-Token', window.CSRFToken); // TODO: This line can be removed once CSRF protection is removed from the backend (not needed here)
      return headers;
    },
    credentials: 'same-origin',
  }),
  tagTypes: ['drugReferences'],
  endpoints: (builder) => ({
    getMedicineNamesByIds: builder.query<Record<string, string>, string[]>({
      query: (ids) => {
        const params = new URLSearchParams();
        ids.forEach((id) => params.append('ids', id));
        return {
          url: `${ENDPOINTS.fromIds}?${params.toString()}`,
          method: 'GET',
        };
      },
    }),

    searchVidalDrugReferences: builder.query<VidalDrugReference[], string>({
      query: (searchString) => ({
        url: ENDPOINTS.search,
        method: 'GET',
        params: { query: searchString },
      }),
      transformResponse: (response: QuerySearchResponse) => response?.entries,
      keepUnusedDataFor: 0, // No need for caching, too inefficient for wide variety of search results
    }),

    // We add a drug reference by providing the vidal_id, which turns into a full DrugReference on the backend.
    addDrugReference: builder.mutation<VidalDrugReference, { tenantId: string; vidal_id: string; vidal_type: VidalDrugReference['type'] }>({
      query: ({ tenantId, vidal_id, vidal_type }) => ({
        url: ENDPOINTS.add,
        method: 'POST',
        body: { tenant_id: tenantId, vidal_id, vidal_type },
      }),
      invalidatesTags: (_result, _error, { tenantId }) => [
        { type: 'drugReferences', id: `tenant-${tenantId}` },
      ],
    }),

    editDrugReference: builder.mutation<DrugReference, { drugReferenceId: string; preferredRoute: string }>({
      query: ({ drugReferenceId, preferredRoute }) => ({
        url: ENDPOINTS.edit(drugReferenceId),
        method: 'PUT',
        body: { preferred_administration_route: preferredRoute },
      }),
      invalidatesTags: (result: DrugReference, _error) => [
        { type: 'drugReferences', id: `tenant-${result.tenant_id}` },
      ],
    }),

    deleteDrugReference: builder.mutation<void, { tenantId: string; drugReferenceId: string }>({
      query: ({ drugReferenceId }) => ({
        url: ENDPOINTS.delete(drugReferenceId),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { tenantId }) => [
        { type: 'drugReferences', id: `tenant-${tenantId}` },
      ],
    }),

    listDrugReferences: builder.query<DrugReference[], { tenantId: string, includeDeleted: boolean} >({
      query: ({ tenantId, includeDeleted }) => ({
        url: ENDPOINTS.list,
        method: 'GET',
        params: { tenant_id: tenantId, include_deleted: includeDeleted },
      }),
      transformResponse: (response: DrugReference[]) => response?.map(formatDrugReference),
      providesTags: (_result, _error, { tenantId }) => [
        { type: 'drugReferences', id: `tenant-${tenantId}` },
      ],
    }),
  }),
});

export const {
  useGetMedicineNamesByIdsQuery,
  useSearchVidalDrugReferencesQuery,
  useAddDrugReferenceMutation,
  useEditDrugReferenceMutation,
  useDeleteDrugReferenceMutation,
  useListDrugReferencesQuery,
} = drugReferencesApi;
